<template>
	<el-dialog :title="isEditMode ? 'Edit Quote' : 'Add/Edit Quote'" :visible.sync="dialogVisible" width="900px"
		:close-on-click-modal="false" :append-to-body="true" @close="handleClose" custom-class="quote-dialog">
		<div slot="title" class="dialog-header d-flex justify-content-between align-items-center">
			<span>{{ isEditMode ? 'Edit Quote' : 'Add/Edit Quote' }}</span>
			<div class="header-right d-flex align-items-center">
				<!-- SENT tag for older sent versions -->
				<span v-if="showSentTag" class="quote-sent-tag">
					SENT
				</span>

				<span class="quote-status-tag"
					:class="{ 'status-draft': !quoteForm.is_published || quoteForm.is_sent, 'status-published': quoteForm.is_published && !quoteForm.is_sent }">
					{{ getStatusText() }}
				</span>

				<!-- Version display - consistent for both single and multiple versions -->
				<el-dropdown v-if="hasMultipleVersions" @command="handleVersionChange" class="version-dropdown mr-4"
					:disabled="loadingVersion">
					<span class="el-dropdown-link">
						Quote {{ displayVersion }}
						<i class="el-icon-arrow-down el-icon--right"></i>
					</span>
					<el-dropdown-menu slot="dropdown">
						<el-dropdown-item v-for="versionOption in availableVersions" :key="versionOption.value"
							:command="versionOption.value" :class="{ 'current-version': versionOption.isCurrent }">
							Quote {{ versionOption.label }}{{ versionOption.isCurrent ? ' (Current)' : '' }}
						</el-dropdown-item>
					</el-dropdown-menu>
				</el-dropdown>

				<!-- Static version display for single versions -->
				<span v-else class="quote-version mr-4">
					Quote {{ displayVersion }}
				</span>
			</div>
		</div>

		<!-- Modal Notification -->
		<div v-if="modalNotification.show" class="modal-notification" :class="modalNotification.type">
			<i :class="getNotificationIcon(modalNotification.type)"></i>
			<span>{{ modalNotification.message }}</span>
			<button @click="hideModalNotification" class="notification-close">
				<i class="el-icon-close"></i>
			</button>
		</div>

		<div class="quote-form-container">
			<el-tabs v-model="activeTab" type="card" @tab-click="handleTabChange">
				<!-- Summary Tab -->
				<el-tab-pane label="Summary" name="summary">
					<div class="tab-content">
						<quote-summary-form v-model="quoteForm" :available-venues="availableVenues"
							:is-edit-mode="isEditMode" :loading="loadingData" ref="quoteSummary" />
					</div>
				</el-tab-pane>

				<!-- Inclusions Tab -->
				<el-tab-pane label="Inclusions" name="inclusions">
					<div class="tab-content">
						<el-form label-position="top">
							<el-form-item label="What's Included">
								<html-editor name="quote_inclusions" :value="quoteForm.inclusions"
									:excludedFormatters="['image']" @input="quoteForm.inclusions = $event"
									placeholder="List what's included in this quote..."></html-editor>
							</el-form-item>
						</el-form>
					</div>
				</el-tab-pane>

				<!-- Payment Terms Tab -->
				<el-tab-pane label="Payment Terms" name="payment-terms">
					<div class="tab-content">
						<el-form label-position="top">
							<el-form-item label="Payment Terms & Conditions">
								<html-editor name="quote_payment_terms" :value="quoteForm.payment_terms"
									:excludedFormatters="['image']" @input="quoteForm.payment_terms = $event"
									placeholder="Enter payment terms and conditions..."
									class="tall-editor"></html-editor>
							</el-form-item>
						</el-form>
					</div>
				</el-tab-pane>

				<!-- Additional Terms Tab -->
				<el-tab-pane label="Additional Terms" name="additional-terms" v-if="false">
					<div class="tab-content">
						<!-- Quote Terms Display Section -->
						<div class="quote-terms-section">
							<h4>Contract Terms & Conditions</h4>
							<quote-terms-display :default-contract="defaultContract"
								:additional-terms="quoteForm.additional_terms" />
						</div>
					</div>
				</el-tab-pane>

				<!-- Attachments Tab -->
				<el-tab-pane label="Attachments" name="attachments">
					<div class="tab-content">
						<div class="attachments-section">
							<!-- File Upload Area -->
							<div class="upload-area">
								<el-upload :before-upload="handleFileUpload"
                                    :show-file-list="false"
									accept="application/pdf, image/png, image/jpeg" action="#"
                                    drag
									multiple
									class="upload-dragger"
                                >
									<div class="upload-content">
										<i class="el-icon-upload"></i>
										<div class="el-upload__text">
											<strong>Drag & Drop or Browse to Upload Files</strong>
											<br>
											<span class="upload-hint">(Supports PDF and Image i.e; .png &amp; .jpeg
												files)</span>
										</div>
									</div>
								</el-upload>
							</div>

							<!-- Selected Attachments -->
							<div class="selected-attachments"
								v-if="quoteForm.files.length > 0 || existingFiles.length > 0">
								<h4>Selected Attachments</h4>
								<ul class="attachment-list">
									<!-- New files -->
									<li v-for="(file, index) in quoteForm.files" :key="`new-${index}`"
										class="attachment-item">
										<i class="fa fa-file-pdf text-danger"></i>
										<span @click="viewFile(file, true)" class="attachment-name" style="cursor: pointer; text-decoration: underline;">{{ file.name }}</span>
										<span class="file-size">({{ formatFileSize(file.size) }})</span>
										<button @click="removeNewFile(index)" class="remove-btn">
											<i class="fa fa-times text-danger"></i> Remove
										</button>
									</li>
									<!-- Existing files (for edit mode) -->
									<li v-for="(file, index) in existingFiles" :key="`existing-${index}`"
										class="attachment-item">
										<i class="fa fa-file-pdf text-danger"></i>
										<span @click="viewFile(file)" class="attachment-name" style="cursor: pointer; text-decoration: underline;">{{ file.original_filename || `File ${index + 1}` }}</span>
										<span class="existing-label">(Current)</span>
										<button @click="removeExistingFile(index)" class="remove-btn">
											<i class="fa fa-times text-danger"></i> Remove
										</button>
									</li>
								</ul>
							</div>
						</div>
					</div>
				</el-tab-pane>
			</el-tabs>
		</div>

		<div slot="footer" class="dialog-footer">
			<el-button @click="handleClose">Cancel</el-button>

			<!-- Hide action buttons for older versions (read-only) -->
			<template v-if="!isViewingOlderVersion">
				<!-- Show buttons on the final tab (attachments) -->
				<template v-if="activeTab === 'attachments'">
					<!-- Save as Draft - show for all states -->
					<el-button @click="submitQuote(false)" :loading="submitting">
						Save as Draft
					</el-button>

					<!-- Save Finalized - only show if not already published -->
					<el-button type="primary" @click="submitQuote(true)" :loading="submitting"
						v-if="!quoteForm.is_published || (quoteForm.is_published && quoteForm.is_sent)">
						Save Finalized
					</el-button>

					<!-- Send Quote - show if published and no form changes -->
					<el-button type="success" @click="sendQuote()" :loading="submitting" v-if="canShowSendButton">
						Send Quote
					</el-button>
				</template>

				<!-- Single button for other tabs -->
				<el-button v-else type="primary" @click="submitQuote(false)" :loading="submitting">
					{{ getSubmitButtonText() }}
				</el-button>
			</template>
		</div>

		<!-- File Viewer Modal -->
		<el-dialog 
			:visible.sync="showAttachmentModal" 
			:title="viewingAttachment.name"
			width="90%"
			top="5vh"
			:append-to-body="true"
			custom-class="attachment-viewer-dialog"
			@close="closeAttachmentModal">
			<div style="width: 100%; height: 80vh; display: flex; justify-content: center; align-items: center; background: #f8f9fa; overflow: auto;">
				<img v-if="viewingAttachment.type === 'image'" :src="viewingAttachment.url" style="max-width: 100%; max-height: 100%; object-fit: contain;" />
				<iframe v-if="viewingAttachment.type === 'pdf'" :src="viewingAttachment.url" width="100%" height="100%" style="border: none;"></iframe>
			</div>
		</el-dialog>

	</el-dialog>
</template>

<script>
import {
	Dialog,
	Form,
	FormItem,
	Input,
	Select,
	Option,
	Button,
	Upload,
	Row,
	Col,
	Switch,
	Tabs,
	TabPane,
	DatePicker,
	Message,
	Dropdown,
	DropdownMenu,
	DropdownItem
} from "element-ui";

import QuoteSummaryForm from "./QuoteSummaryForm.vue";
import HtmlEditor from "@/components/Inputs/HtmlEditor.vue";
import QuoteTermsDisplay from "./QuoteTermsDisplay.vue";
import QuoteService from "@/services/quote.service";
import moment from 'moment';
import momenttz from 'moment-timezone';

export default {
	name: "CreateUpdateQuote",
	components: {
		[Dialog.name]: Dialog,
		[Form.name]: Form,
		[FormItem.name]: FormItem,
		[Input.name]: Input,
		[Select.name]: Select,
		[Option.name]: Option,
		[Button.name]: Button,
		[Upload.name]: Upload,
		[Row.name]: Row,
		[Col.name]: Col,
		[Switch.name]: Switch,
		[Tabs.name]: Tabs,
		[TabPane.name]: TabPane,
		[DatePicker.name]: DatePicker,
		[Dropdown.name]: Dropdown,
		[DropdownMenu.name]: DropdownMenu,
		[DropdownItem.name]: DropdownItem,
		QuoteSummaryForm,
		[HtmlEditor.name]: HtmlEditor,
		QuoteTermsDisplay,

	},
	props: {
		visible: {
			type: Boolean,
			default: false
		},
		leadEvent: {
			type: Object,
			required: true
		},
		availableVenues: {
			type: Array,
			default: () => []
		},
		quoteToEdit: {
			type: Object,
			default: null
		},
		quotesData: {
			type: [Object, null],
			default: null
		}
	},
	data() {
		return {
			submitting: false,
			activeTab: 'summary',
			quoteForm: {
				quote_id: '',
				name: '',
				event_type: '',
				venue_id: '',
				event_date: '',
				valid_till: '',
				status: 'draft',
				total_cost: '',
				inclusions: '',
				payment_terms: '',
				files: [],
				version: '',
				is_published: false,
				is_draft: false,
				is_sent: false,
				additional_terms: []
			},
			existingFiles: [],
			filesToRemove: [],
			defaultContract: null,
			originalFormData: null, // Store original form data to track changes
			loadingVersion: false, // Track loading state when fetching version data
			maxVersion: null, // Track the highest version available for this venue
			originalEditingQuote: null, // Track the original quote context for consistent status display
			modalNotification: {
				show: false,
				type: 'success', // 'success', 'warning', 'error'
				message: '',
				timeout: null
			},
			_updatingFromQuotesData: false, // Flag to prevent infinite update loops
			allowedFileTypes: ['application/pdf', 'image/png', 'image/jpeg'],
			loadingData: false,
			showAttachmentModal: false,
			viewingAttachment: {
				type: null,
				url: null,
				name: null
			}
		};
	},
	computed: {
		dialogVisible: {
			get() {
				return this.visible;
			},
			set(value) {
				this.$emit('update:visible', value);
			}
		},
		isEditMode() {
			return !!this.quoteToEdit;
		},
		hasFormChanges() {
			if (!this.originalFormData || !this.quoteForm.is_published) {
				return false;
			}

			// Check if key form fields have changed from original
			const fieldsToCheck = ['name', 'total_cost', 'inclusions', 'payment_terms', 'valid_till'];

			for (let field of fieldsToCheck) {
				if (this.quoteForm[field] != this.originalFormData[field]) {
					return true;
				}
			}

			// Check if files have been added or removed
			if (this.quoteForm.files.length > 0 || this.filesToRemove.length > 0) {
				return true;
			}

			return false;
		},
		canShowSendButton() {
			return this.quoteForm.is_published &&
				!this.hasFormChanges &&
				!this.quoteForm.is_sent;
		},
		// Display version for dropdown options (shows the actual version being viewed)
		displayVersion() {
			const version = parseInt(this.quoteForm.version) || 1;
			return `v${version}.0`;
		},

		// Editing version (shows what version user is working on - incremented if editing sent quote)
		editingVersion() {
			let version = parseInt(this.maxVersion) || 1;
			return `v${version}.0`;
		},

		// Version number to use when saving (incremented if quote is sent)
		versionForSaving() {
			let version = parseInt(this.maxVersion) || 1;
			return version;
		},

		// For backward compatibility - use editingVersion
		version() {
			return this.editingVersion;
		},
		availableVersions() {
			// Use maxVersion if available, otherwise fall back to current form version
			const maxVer = this.maxVersion || parseInt(this.quoteForm.version) || 1;

			if (maxVer <= 1) {
				return [];
			}

			// Current version should be incremented if the quote is sent.
			let currentVersion = parseInt(this.quoteForm.version) || 1;

			// Generate version options from max version down to 1 (reverse order for better UX)
			const versions = [];
			for (let i = maxVer; i >= 1; i--) {
				versions.push({
					value: i,
					label: `v${i}.0`,
					version: i,
					isCurrent: i === currentVersion
				});
			}

			return versions;
		},
		hasMultipleVersions() {
			const maxVer = this.maxVersion || parseInt(this.quoteForm.version) || 1;
			return maxVer > 1;
		},
		selectedVersionValue() {
			return parseInt(this.quoteForm.version) || 1;
		},
		isViewingOlderVersion() {
			// Check if we're viewing an older version (not the latest)
			const currentVersion = parseInt(this.quoteForm.version) || 1;
			const maxVer = this.maxVersion || currentVersion;
			return currentVersion < maxVer;
		},
		showSentTag() {
			// Show SENT tag for older versions that are actually sent
			// Don't show for the current working version (maxVersion)
			const currentVersion = parseInt(this.quoteForm.version) || 1;
			const maxVer = this.maxVersion || 1;

			return this.quoteForm.is_sent && currentVersion < maxVer;
		}
	},
	watch: {
		visible(newVal) {
			if (newVal) {
				this.initializeForm();
			}
		},
		quoteToEdit: {
			handler() {
				if (this.quoteToEdit) {
					this.initializeForm();
				}
			},
			immediate: true
		},
		'quoteForm.venue_id': {
			handler(newVenueId, oldVenueId) {
				if (newVenueId && newVenueId !== oldVenueId) {
					this.$nextTick(() => {
						this.handleVenueChange(newVenueId);
					});
				} else if (!newVenueId && oldVenueId) {
					this.quoteForm.additional_terms = [];
				}
			}
		},
		hasFormChanges: {
			handler(hasChanges) {
				// If a published quote has been edited, change it back to draft status
				if (hasChanges && this.quoteForm.is_published && !this.quoteForm.is_sent) {
					// this.quoteForm.is_published = false;
					this.quoteForm.is_draft = true;
					this.quoteForm.status = 'draft';
				}
			},
			immediate: false
		},
		quotesData: {
			handler(newQuotesData, oldQuotesData) {
				// Prevent infinite loops by checking if data actually changed
				if (!newQuotesData || newQuotesData === oldQuotesData) {
					return;
				}

				// Only process if dialog is visible and we're not already updating
				if (!this.visible || this._updatingFromQuotesData) {
					return;
				}

				// Set flag to prevent recursive updates
				this._updatingFromQuotesData = true;
				this.loadingData = true;

				try {
					// Update default contract if available and different
					if (newQuotesData.default_contract &&
						JSON.stringify(newQuotesData.default_contract) !== JSON.stringify(this.defaultContract)) {
						this.defaultContract = newQuotesData.default_contract;
					}

					// If a venue is selected, check for updated quote data
					if (this.quoteForm.venue_id && newQuotesData.quotes_by_venue) {
						const venueIdStr = this.quoteForm.venue_id.toString();
						const venueData = newQuotesData.quotes_by_venue[venueIdStr];
						const oldVenueData = oldQuotesData?.quotes_by_venue?.[venueIdStr];
						if (venueData && JSON.stringify(venueData) !== JSON.stringify(oldVenueData)) {
							// Update venue additional terms only if they changed
							if (venueData.venue_additional_terms && Array.isArray(venueData.venue_additional_terms)) {
								const newTerms = venueData.venue_additional_terms
									.sort((a, b) => (a.order || 0) - (b.order || 0))
									.map(term => ({ ...term }));

								if (JSON.stringify(newTerms) !== JSON.stringify(this.quoteForm.additional_terms)) {
									this.quoteForm.additional_terms = newTerms;
								}
							}

							// Check if there's an updated active quote
							if (venueData.active_quote) {
								const activeQuote = venueData.active_quote;
								const currentQuoteId = this.quoteForm.quote_id ? this.quoteForm.quote_id.toString() : '';
								const activeQuoteId = activeQuote.id ? activeQuote.id.toString() : '';
								const oldActiveQuote = oldVenueData?.active_quote;

								// Only update if this is the same quote or no quote is loaded, and the active quote actually changed
								if ((! currentQuoteId || currentQuoteId !== activeQuoteId)) {

									// Check if key fields actually changed before updating
									const hasChanges = (
										this.quoteForm.name !== (activeQuote.name || '') ||
										this.quoteForm.total_cost !== (activeQuote.total_amount || '') ||
										this.quoteForm.inclusions !== (activeQuote.inclusions || '') ||
										this.quoteForm.payment_terms !== (activeQuote.payment_terms || '') ||
										this.quoteForm.valid_till !== (activeQuote.valid_till || '') ||
										this.quoteForm.is_published !== (activeQuote.is_published || false) ||
										this.quoteForm.is_sent !== (activeQuote.is_sent || false) ||
										this.quoteForm.version !== (activeQuote.version || '') ||
										this.quoteForm.quote_id !== (activeQuote.id || '')
									);

									if (hasChanges) {
										// Ensure we preserve the venue_id if it's missing in the active quote
										if (! activeQuote.venue_id && this.quoteForm.venue_id) {
											activeQuote.venue_id = this.quoteForm.venue_id;
										}
										this.initializeFormWithData(activeQuote);
									}
								}
							}
						}
					}
				} catch (error) {
					console.error('Error updating from quotesData:', error);
				} finally {
					// Clear the update flag after a short delay to allow for any pending updates
					this.$nextTick(() => {
						this._updatingFromQuotesData = false;
						this.loadingData = false;
					});
				}
			},
			deep: true,
			immediate: false
		}

	},
	methods: {
		viewFile(file, isNew = false) {
			let url = null;
			let name = null;
			let type = null;

			if (isNew) {
				url = URL.createObjectURL(file);
				name = file.name;
				type = file.type.includes('image') ? 'image' : 'pdf';
			} else {
				url = file.full_url || file.file_path;
				name = file.original_filename || file.file_name;
				// Determine type from file extension or backend data if available
				// simplistic check:
				const ext = name.split('.').pop().toLowerCase();
				if (['png', 'jpg', 'jpeg', 'gif'].includes(ext)) {
					type = 'image';
				} else {
					type = 'pdf';
				}
			}

			this.viewingAttachment = {
				type,
				url,
				name
			};
			this.showAttachmentModal = true;
		},

		closeAttachmentModal() {
			this.showAttachmentModal = false;
			this.viewingAttachment = {
				type: null,
				url: null,
				name: null
			};
		},

		// Helper function to convert valid_till date to end of day in user timezone, then to UTC
		convertValidTillToUTC(validTillDate) {
			if (!validTillDate) return '';

			try {
				// Get user's actual browser timezone
				const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

				// Create end of day time (23:59:59) in user's timezone
				const endOfDayInUserTz = momenttz.tz(
					moment(validTillDate).format('YYYY-MM-DD') + ' 23:59:59',
					'YYYY-MM-DD HH:mm:ss',
					userTimezone
				);

				// Convert to UTC and format as ISO string
				const utcResult = endOfDayInUserTz.utc().format();
				return utcResult;
			} catch (error) {
				console.error('Error converting valid_till to UTC:', error);
				// Fallback: return the original date if conversion fails
				return validTillDate;
			}
		},

		// Helper function to properly handle is_draft logic
		// Returns false only if value is explicitly false or 'false', otherwise true
		// This ensures: false/'false' = false, null/undefined/true/'true'/any other value = true
		parseDraftStatus(value) {
			return !(value === false || value === 'false');
		},

		showModalNotification(type, message) {
			// Clear any existing timeout
			if (this.modalNotification.timeout) {
				clearTimeout(this.modalNotification.timeout);
			}

			this.modalNotification = {
				show: true,
				type: type,
				message: message,
				timeout: null
			};

			// Auto-hide after 4 seconds
			this.modalNotification.timeout = setTimeout(() => {
				this.hideModalNotification();
			}, 4000);
		},

		hideModalNotification() {
			if (this.modalNotification.timeout) {
				clearTimeout(this.modalNotification.timeout);
			}
			this.modalNotification.show = false;
		},

		getNotificationIcon(type) {
			switch (type) {
				case 'success':
					return 'el-icon-success';
				case 'warning':
					return 'el-icon-warning';
				case 'error':
					return 'el-icon-error';
				default:
					return 'el-icon-info';
			}
		},

		initializeFormWithData(quoteData) {

			// Populate form with existing quote data
			this.quoteForm = {
				quote_id: quoteData.id || '',
				name: quoteData.name || '',
				event_type: quoteData.event_type || this.leadEvent.event_type || '',
				venue_id: quoteData.venue_id ? parseInt(quoteData.venue_id) : '',
				event_date: quoteData.event_date || this.leadEvent.event_start?.split(' ')[0] || '',
				valid_till: quoteData.valid_till || '',
				status: quoteData.status || 'draft',
				total_cost: quoteData.total_amount || quoteData.total_cost || '',
				inclusions: quoteData.inclusions || '',
				payment_terms: quoteData.payment_terms || quoteData.terms || '',
				files: [],
				version: quoteData.version || '',
				is_published: quoteData.is_published || false,
				is_draft: this.parseDraftStatus(quoteData.is_draft),
				is_sent: quoteData.is_sent || false,
				additional_terms: []
			};

			// Store original editing context for consistent status display
			this.originalEditingQuote = {
				version: quoteData.version || '',
				is_sent: quoteData.is_sent || false,
				is_published: quoteData.is_published || false
			};

			// Set maxVersion: if current version is sent, draft version (version + 1) is the max
			// If current version is draft, it's already the max version
			const currentVersion = parseInt(quoteData.version) || 1;
			this.maxVersion = quoteData.is_sent ? currentVersion + 1 : currentVersion;

			// If the loaded quote is sent, we should be working on the next version (draft)
			if (quoteData.is_sent) {
				this.quoteForm.version = this.maxVersion.toString();
				this.quoteForm.is_sent = false; // New version is not sent
				this.quoteForm.is_draft = true; // New version is draft
				this.quoteForm.is_published = false; // New version is not published
				this.quoteForm.status = 'draft'; // New version status
			}

			// Store existing files for display
			this.existingFiles = [];
			this.filesToRemove = [];

			const filesList = quoteData.attachments;
			if (filesList && Array.isArray(filesList)) {
				this.existingFiles = filesList.map(file => ({
					id: file.id || file.file_id,
					file_id: file.id || file.file_id,
					original_filename: file.file_name || file.original_filename,
					file_path: file.file_path,
					full_url: file.full_url,
					file_type: file.file_type
				}));
			}

			// Load venue data
			this.$nextTick(() => {
				this.loadVenueData();
				// Store original form data after loading for change tracking
				this.storeOriginalFormData();
			});
		},

		initializeForm() {
			if (this.isEditMode && this.quoteToEdit) {
				this.initializeFormWithData(this.quoteToEdit);
			} else {
				// Reset form for new quote with some defaults from lead event
				this.resetForm();
				if (this.leadEvent) {
					this.quoteForm.event_type = this.leadEvent.event_type || '';
					this.quoteForm.event_date = this.leadEvent.event_start?.split(' ')[0] || '';
					this.quoteForm.name = this.leadEvent.name || '';
				}

				// Load venue data (including default contract)
				this.$nextTick(() => {
					this.loadVenueData();
				});
			}
		},

		resetForm() {
			this.quoteForm = {
				quote_id: '',
				name: '',
				event_type: '',
				venue_id: '',
				event_date: '',
				valid_till: '',
				status: 'draft',
				total_cost: '',
				inclusions: '',
				payment_terms: '',
				files: [],
				version: '',
				is_published: false,
				is_draft: true,
				additional_terms: []
			};
			this.existingFiles = [];
			this.filesToRemove = [];
			this.defaultContract = null;
			this.originalFormData = null;
			this.activeTab = 'summary';
			this.maxVersion = null;
			this.originalEditingQuote = null;
		},

		handleFileUpload(file) {
			if (file && this.allowedFileTypes.includes(file.type)) {
				// Check if file already exists
				const isDuplicate = this.quoteForm.files.find(existingFile => {
					return existingFile.name === file.name && existingFile.size === file.size;
                });

				if (isDuplicate) {
					this.showModalNotification('warning', 'This file has already been selected');
					return false;
				}

				this.quoteForm.files.push(file);
				this.showModalNotification('success', `Added ${file.name}`);
			} else if (file) {
				this.showModalNotification('error', 'Please select a valid file (PDF, PNG, or JPEG)');
			}
			return false; // Prevent auto upload
		},

		removeNewFile(index) {
			const fileName = this.quoteForm.files[index]?.name;
			this.quoteForm.files.splice(index, 1);
			if (fileName) {
				this.showModalNotification('success', `Removed ${fileName}`);
			}
		},

		removeExistingFile(index) {
			const file = this.existingFiles[index];
			if (file) {
				const fileId = file.id || file.file_id;
				this.filesToRemove.push(fileId);
				this.existingFiles.splice(index, 1);
				this.showModalNotification('success', `Marked ${file.original_filename || 'file'} for removal`);
			}
		},

		formatFileSize(bytes) {
			if (bytes === 0) return '0 Bytes';
			const k = 1024;
			const sizes = ['Bytes', 'KB', 'MB', 'GB'];
			const i = Math.floor(Math.log(bytes) / Math.log(k));
			return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
		},

		handleTabChange(tab) {
			console.log('Tab changed to:', tab.name);
		},

		getSubmitButtonText() {
			if (this.activeTab === 'summary') {
				return 'Save & Next';
			} else if (this.activeTab === 'attachments') {
				return this.isEditMode ? 'Update Quote' : 'Save & Preview';
			} else {
				return 'Save & Next';
			}
		},

		getStatusText() {
			// Show simple Published/Draft status for all versions
			if (this.quoteForm.is_published && !this.quoteForm.is_sent) {
				return 'Published';
			} else {
				return 'Draft';
			}
		},

		async submitQuote(published = null) {
			// Validation for final submission
			if (!this.quoteForm.venue_id) {
				this.showModalNotification('error', 'Please select a venue');
				return;
			}

			if (!this.quoteForm.total_cost) {
				this.showModalNotification('error', 'Please enter a total cost');
				return;
			}

			this.submitting = true;
			try {
				const formData = new FormData();
				formData.append('name', this.quoteForm.name || '');
				formData.append('event_type', this.quoteForm.event_type || '');
				formData.append('venue_id', this.quoteForm.venue_id);
				formData.append('event_date', this.quoteForm.event_date || '');

				// Convert valid_till to end of day in user timezone, then to UTC
				const validTillUTC = this.convertValidTillToUTC(this.quoteForm.valid_till);
				formData.append('valid_till', validTillUTC);

				formData.append('total_cost', this.quoteForm.total_cost || '');
				formData.append('inclusions', this.quoteForm.inclusions || '');
				formData.append('payment_terms', this.quoteForm.payment_terms || '');

				// Add version - use versionForSaving to handle sent quote logic
				formData.append('version', this.versionForSaving);

				// Add published parameter for final submissions
				if (published !== null) {
					formData.append('published', published ? 1 : 0);
				}

				// Set is_draft based on  status (applies to both create and edit)
				let isDraft;
				if (published !== null) {
					// If published is explicitly set, is_draft should be the opposite
					isDraft = !published;
				} else {
					// If published is not set, use the existing is_draft value (for edit mode) or default to true (for create mode)
					isDraft = this.isEditMode ? this.quoteForm.is_draft : true;
				}
				formData.append('is_draft', isDraft ? 1 : 0);

				// Add multiple files
				if (this.quoteForm.files && this.quoteForm.files.length > 0) {
					this.quoteForm.files.forEach((file, index) => {
						formData.append(`quote_files[]`, file);
					});
				}

				// Add files to remove (for both edit mode and new quotes with prefilled data)
				if (this.filesToRemove.length > 0) {
					this.filesToRemove.forEach(fileId => {
						formData.append('files_to_remove[]', fileId);
					});
				}

				// Add existing attachment IDs to keep (for edit mode and quotes with prefilled data)
				if (this.existingFiles.length > 0) {
					this.existingFiles.forEach(file => {
						const fileId = file.id || file.file_id;
						if (fileId) {
							formData.append('existing_files[]', fileId);
						}
					});
				}

				let response;
				if (this.isEditMode) {
					response = await this.$store.dispatch("leadEvent/createQuote", {
						leadEventId: this.leadEvent.id,
						quoteId: this.quoteToEdit.id,
						formData: formData
					});
				} else {
					response = await this.$store.dispatch("leadEvent/createQuote", {
						leadEventId: this.leadEvent.id,
						formData: formData
					});
				}

				// Only proceed if the request was successful
				if (response && response.success !== false) {
					// Show success message based on action
					let successMessage = '';
					if (published === true) {
						successMessage = 'Quote finalized and saved successfully!';
					} else if (published === false) {
						successMessage = 'Quote saved as draft successfully!';
					} else {
						successMessage = 'Quote updated successfully!';
					}
					this.showModalNotification('success', successMessage);

					// If quote was published, update original form data for change tracking
					if (published === true) {
						this.quoteForm.is_published = true;
						this.quoteForm.is_draft = false;
						this.storeOriginalFormData();
					}

					if (this.activeTab !== 'attachments' && published !== true) {
						// This is a "Save & Next" action from earlier tabs
						this.moveToNextTab();
						return;
					}

					// If quote was finalized (published=true) on attachments tab, keep modal open for immediate sending
					if (this.activeTab === 'attachments' && published === true) {
						// Don't close modal - user can now immediately send the quote
						this.$emit('quote-saved');
						return;
					}

					// Close dialog for draft saves or other cases
					this.$emit('quote-saved');
					this.handleClose();
				} else {
					// Handle case where response indicates failure
					throw new Error(response?.message || 'Request failed');
				}
			} catch (error) {
				console.error("Error saving quote:", error);

				// Extract error message from different response formats
				let errorMessage = `Failed to ${this.isEditMode ? 'update' : 'add'} quote`;

				if (error?.response?.data?.message) {
					errorMessage = error.response.data.message;
				} else if (error?.message && error.message !== 'Request failed') {
					errorMessage = error.message;
				} else if (typeof error === 'string') {
					errorMessage = error;
				}

				this.showModalNotification('error', errorMessage);
			} finally {
				this.submitting = false;
			}
		},

		moveToNextTab() {
			// const tabs = ['summary', 'inclusions', 'payment-terms', 'additional-terms', 'attachments'];
			const tabs = ['summary', 'inclusions', 'payment-terms', 'attachments'];
			const currentIndex = tabs.indexOf(this.activeTab);
			if (currentIndex < tabs.length - 1) {
				this.activeTab = tabs[currentIndex + 1];
			}
		},

		handleClose() {
			this.dialogVisible = false;
			this.resetForm();
			if (this.$refs.quoteSummary) {
				this.$refs.quoteSummary.resetValidation();
			}
			this.$emit('close');
		},

		async handleVenueChange(newVenueId) {
			this.loadingData = true;
			try {
				// First load venue data (additional terms, etc.)
				await this.loadVenueData();

				// Check if there's an existing active quote for this venue
				await this.checkAndPrefillExistingQuote(newVenueId);
			} finally {
				this.loadingData = false;
			}
		},

		async checkAndPrefillExistingQuote(venueId) {
			if (!this.quotesData || !this.quotesData.quotes_by_venue) {
				return;
			}

			try {
				// Ensure venueId is treated as string for consistent comparison
				const venueIdStr = venueId.toString();

				// Check if there's venue data for this venue
				const venueData = this.quotesData.quotes_by_venue[venueIdStr];
				if (!venueData || !venueData.active_quote) {

					// Clear form fields when no active quote exists for this venue
					this.quoteForm = {
						...this.quoteForm, // Keep venue_id and additional_terms
						name: this.leadEvent.name || '', // Default to lead event name
						total_cost: '',
						inclusions: '',
						payment_terms: '',
						valid_till: '',
						status: 'draft',
						version: '',
						is_published: false,
						is_draft: true,
						is_sent: false,
						files: [] // Clear any new files
					};

					// Clear existing files
					this.existingFiles = [];

					// Set maxVersion to 1 when no active quote exists (new quote scenario)
					this.maxVersion = 1;

					return;
				}

				const activeQuote = venueData.active_quote;

				// Prefill the form with the active quote data (excluding additional_terms)
				// Ensure we preserve the venue_id if it's missing in the active quote
				if (! activeQuote.venue_id && venueId) {
					activeQuote.venue_id = venueId;
				}

				this.initializeFormWithData(activeQuote);

			} catch (error) {
				console.warn('Error checking for existing quote:', error);
			}
		},

		async loadVenueData() {
			try {
				if (this.quotesData && this.quotesData.default_contract) {
					this.defaultContract = this.quotesData.default_contract;
				} else {
					const SettingsService = await import('@/services/settings.service');
					const contractResponse = await SettingsService.default.getDefaultContract();
					if (contractResponse && contractResponse.data) {
						this.defaultContract = contractResponse.data;
					}
				}

				// Only load venue-specific terms if a venue is selected
				if (!this.quoteForm.venue_id) {
					this.quoteForm.additional_terms = [];
					return;
				}

				const selectedVenueId = this.quoteForm.venue_id.toString();

				if (this.quotesData && this.quotesData.quotes_by_venue) {
					const venueData = this.quotesData.quotes_by_venue[selectedVenueId];

					if (venueData && venueData.venue_additional_terms && Array.isArray(venueData.venue_additional_terms) && venueData.venue_additional_terms.length > 0) {
						this.quoteForm.additional_terms = venueData.venue_additional_terms
							.sort((a, b) => (a.order || 0) - (b.order || 0))
							.map(term => ({ ...term }));
						return;
					}
				}
			} catch (error) {
				console.warn('Could not load venue data, using defaults:', error);
				this.quoteForm.additional_terms = [];
			}
		},

		storeOriginalFormData() {
			if (this.quoteForm.is_published) {
				// Store a deep copy of the form data for change tracking
				this.originalFormData = {
					name: this.quoteForm.name,
					total_cost: this.quoteForm.total_cost,
					inclusions: this.quoteForm.inclusions,
					payment_terms: this.quoteForm.payment_terms,
					valid_till: this.quoteForm.valid_till
				};
			} else {
				this.originalFormData = null;
			}
		},

		async sendQuote() {
			if (!this.quoteForm.quote_id) {
				this.showModalNotification('error', 'Quote ID is required to send quote');
				return;
			}

			this.submitting = true;
			try {
				const response = await QuoteService.sendQuote(this.quoteForm.quote_id);

				if (response && response.success !== false) {
					this.showModalNotification('success', 'Quote sent successfully!');
					this.$emit('quote-sent');
					this.handleClose();
				} else {
					throw new Error(response?.message || 'Failed to send quote');
				}
			} catch (error) {
				console.error("Error sending quote:", error);

				let errorMessage = 'Failed to send quote';
				if (error?.response?.data?.message) {
					errorMessage = error.response.data.message;
				} else if (error?.message && error.message !== 'Failed to send quote') {
					errorMessage = error.message;
				}

				this.showModalNotification('error', errorMessage);
			} finally {
				this.submitting = false;
			}
		},

		async handleVersionChange(selectedVersion) {
			if (!selectedVersion || !this.quoteForm.venue_id) {
				return;
			}

			// If user selected the current version, no need to fetch
			const currentVersion = parseInt(this.quoteForm.version);
			if (selectedVersion === currentVersion) {
				return;
			}

			this.loadingVersion = true;

			const versionToFetch = (selectedVersion == this.maxVersion && this.originalEditingQuote.is_sent)
				? selectedVersion - 1
				: selectedVersion;

			try {
				// Fetch the selected version data from backend
				const response = await QuoteService.getQuoteByVersion(
					this.leadEvent.id,
					this.quoteForm.venue_id,
					versionToFetch
				);

				if (response && response.data) {
					const quoteData = response.data;

					// Update form with selected version data
					this.quoteForm = {
						...this.quoteForm,
						name: quoteData.name || '',
						total_cost: quoteData.total_amount || '',
						inclusions: quoteData.inclusions || '',
						payment_terms: quoteData.payment_terms || '',
						valid_till: quoteData.valid_till || '',
						status: quoteData.status || 'draft',
						version: selectedVersion.toString(),
						is_published: quoteData.is_published || false,
						is_draft: this.parseDraftStatus(quoteData.is_draft),
						is_sent: quoteData.is_sent || false,
						// Use additional_terms from the quote version response, not venue's current terms
						additional_terms: quoteData.additional_terms || []
					};

					// Use default_contract from the quote version response, not current default contract
					if (quoteData.default_contract) {
						this.defaultContract = quoteData.default_contract;
					}

					// Handle files if they exist
					this.existingFiles = [];
					if (quoteData.attachments && Array.isArray(quoteData.attachments)) {
						this.existingFiles = quoteData.attachments.map(attachment => ({
							id: attachment.id,
							file_id: attachment.id,
							original_filename: attachment.file_name || attachment.original_filename,
							file_path: attachment.file_path,
							file_type: attachment.file_type
						}));
					}

					// Clear any pending file removals and new files
					this.filesToRemove = [];
					this.quoteForm.files = [];

					// Store original form data for change tracking
					this.$nextTick(() => {
						this.storeOriginalFormData();
					});

					this.showModalNotification('success', `Loaded Quote v${selectedVersion}.0`);
				}
			} catch (error) {
				console.error("Error loading quote version:", error);
				this.showModalNotification('error', 'Failed to load selected quote version');

				// Reset dropdown to current version on error
				this.$nextTick(() => {
					// The v-model will automatically revert due to computed property
				});
			} finally {
				this.loadingVersion = false;
			}
		}
	}
};
</script>

<style scoped>
.dialog-header {
	width: 100%;
	display: flex;
	justify-content: space-between;
	align-items: center;
}

.header-right {
	display: flex;
	align-items: center;
	gap: 8px;
}

.quote-status-tag {
	font-size: 0.75rem;
	font-weight: 600;
	padding: 2px 10px;
	border-radius: 12px;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	white-space: nowrap;
}

.quote-status-tag.status-draft {
	background-color: #f5f5f5;
	color: #666;
	border: 1px solid #ddd;
}

.quote-status-tag.status-published {
	background-color: #f0f9ff;
	color: #059669;
	border: 1px solid #d1fae5;
}

.quote-version {
	font-size: 0.9rem;
	color: #666;
	background: #f5f5f5;
	padding: 4px 8px;
	border-radius: 4px;
}

.version-dropdown {
	font-size: 0.9rem;
}

.version-dropdown .el-dropdown-link {
	font-size: 0.9rem;
	color: #666;
	background: #f5f5f5;
	padding: 4px 8px;
	border-radius: 4px;
	display: inline-flex;
	align-items: center;
	text-decoration: none;
	cursor: pointer;
}

.version-dropdown .el-dropdown-link:hover {
	background: #e9e9e9;
}

/* When dropdown is disabled (single version), style it like a static element */
.version-dropdown.is-disabled .el-dropdown-link {
	cursor: default;
	background: #f5f5f5;
}

.version-dropdown.is-disabled .el-dropdown-link:hover {
	background: #f5f5f5;
}

.quote-sent-tag {
	display: inline-block;
	padding: 4px 8px;
	font-size: 12px;
	font-weight: 500;
	background-color: #e8f4f8;
	color: #0369a1;
	border: 1px solid #bae6fd;
	border-radius: 12px;
	margin-left: 8px;
}

.version-dropdown .el-dropdown-link i {
	margin-left: 4px;
	font-size: 12px;
}

/* Current version styling */
.current-version {
	background-color: #e6f7ff !important;
	color: #1890ff !important;
	font-weight: 600;
}

.current-version:hover {
	background-color: #d4eefc !important;
}

.modal-notification {
	position: relative;
	display: flex;
	align-items: center;
	padding: 12px 16px;
	margin: 0 20px 15px 20px;
	border-radius: 4px;
	font-size: 14px;
	animation: slideDown 0.3s ease-out;
}

.modal-notification.success {
	background-color: #f0f9ff;
	border: 1px solid #d1fae5;
	color: #065f46;
}

.modal-notification.warning {
	background-color: #fffbeb;
	border: 1px solid #fed7aa;
	color: #92400e;
}

.modal-notification.error {
	background-color: #fef2f2;
	border: 1px solid #fecaca;
	color: #991b1b;
}

.modal-notification i {
	margin-right: 8px;
	font-size: 16px;
}

.modal-notification span {
	flex: 1;
}

.notification-close {
	background: none;
	border: none;
	cursor: pointer;
	margin-left: 8px;
	opacity: 0.6;
	font-size: 12px;
}

.notification-close:hover {
	opacity: 1;
}

@keyframes slideDown {
	from {
		opacity: 0;
		transform: translateY(-10px);
	}

	to {
		opacity: 1;
		transform: translateY(0);
	}
}

.quote-form-container {
	min-height: 500px;
	padding: 15px;
}

.tab-content {
	padding: 20px 0;
	min-height: 400px;
}

.upload-area {
	margin-bottom: 30px;
}

.upload-dragger {
	width: 100%;
}

.upload-dragger .el-upload {
	width: 100%;
}

.upload-dragger .el-upload-dragger {
	width: 100%;
	height: 200px;
	border: 2px dashed #d9d9d9;
	border-radius: 8px;
	background-color: #fafafa;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	transition: all 0.3s ease;
}

.upload-dragger .el-upload-dragger:hover {
	border-color: #409eff;
	background-color: #f0f9ff;
}

.upload-content {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	width: 100%;
	height: 100%;
	padding: 20px;
	text-align: center;
}

.upload-content i {
	font-size: 48px;
	color: #c0c4cc;
	margin-bottom: 16px;
}

.upload-content .el-upload__text {
	color: #606266;
	font-size: 16px;
	line-height: 1.4;
}

.upload-content .el-upload__text strong {
	font-weight: 600;
	color: #303133;
}

.upload-hint {
	color: #8c939d;
	font-size: 14px;
	margin-top: 8px;
}

.selected-attachments {
	margin-top: 20px;
}

.selected-attachments h4 {
	margin-bottom: 16px;
	color: #303133;
	font-size: 16px;
}

.attachment-list {
	list-style: none;
	padding: 0;
	margin: 0;
}

.attachment-item {
	display: flex;
	align-items: center;
	padding: 12px;
	border: 1px solid #dcdfe6;
	border-radius: 4px;
	margin-bottom: 8px;
	background: #f9f9f9;
}

.attachment-item i {
	margin-right: 8px;
}

.attachment-item span {
	flex: 1;
	font-size: 14px;
}

.file-size {
	color: #8c939d;
	font-size: 12px;
	margin-left: 8px;
	flex: none;
}

.existing-label {
	color: #67c23a;
	font-size: 12px;
	background: #f0f9ff;
	padding: 2px 6px;
	border-radius: 3px;
	margin-left: 8px;
	flex: none;
}

.remove-btn {
	background: none;
	border: none;
	color: #f56c6c;
	cursor: pointer;
	font-size: 12px;
	padding: 4px 8px;
}

.remove-btn:hover {
	background: #fef0f0;
	border-radius: 4px;
}

.quote-link-section {
	border-top: 1px solid #ebeef5;
	padding-top: 20px;
}

.quote-metadata-section {
	margin-top: 30px;
	padding-top: 20px;
	border-top: 1px solid #ebeef5;
}

.quote-metadata-section h4 {
	margin-bottom: 16px;
	color: #303133;
	font-size: 16px;
}

.quote-terms-section h4 {
	margin-bottom: 16px;
	color: #303133;
	font-size: 16px;
}

.dialog-footer {
	text-align: right;
	padding-top: 20px;
	border-top: 1px solid #ebeef5;
}

.text-sm {
	font-size: 0.875rem;
}

.text-muted {
	color: #6c757d;
}

.text-info {
	color: #17a2b8;
}

.text-danger {
	color: #f56c6c;
}

.mt-2 {
	margin-top: 0.5rem;
}

.mt-4 {
	margin-top: 1rem;
}

.mr-1 {
	margin-right: 0.25rem;
}

.d-flex {
	display: flex;
}

.justify-content-between {
	justify-content: space-between;
}

.align-items-center {
	align-items: center;
}
</style>

<style>
/* Global styles for the quote dialog */
.quote-dialog {
	max-width: 95vw;
}

.quote-dialog .el-dialog__body {
	padding: 10px 20px 20px;
}

.quote-dialog .el-form-item {
	margin-bottom: 20px;
}

.quote-dialog .el-tabs__content {
	padding: 0;
}

.quote-dialog .el-tabs__nav-wrap::after {
	height: 1px;
	background-color: #e4e7ed;
}

.quote-dialog .el-tabs__item {
	padding: 0 16px;
	font-weight: 500;
}

.quote-dialog .el-tabs__item.is-active {
	color: #409eff;
}

.quote-dialog .el-upload-dragger {
	width: 100%;
	height: 200px;
	border: 2px dashed #d9d9d9;
	border-radius: 8px;
	background-color: #fafafa;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	transition: all 0.3s ease;
}

.dialog-footer {
	text-align: right;
}

.dialog-footer .el-button {
	margin-left: 10px;
}

.dialog-footer .el-button:first-child {
	margin-left: 0;
}

.quote-dialog .el-upload-dragger:hover {
	border-color: #409eff;
	background-color: #f0f9ff;
}

.quote-dialog .el-upload-dragger .el-icon-upload {
	font-size: 48px;
	color: #c0c4cc;
	margin-bottom: 16px;
}

.quote-dialog .el-upload-dragger .el-upload__text {
	color: #606266;
	font-size: 16px;
	text-align: center;
	line-height: 1.4;
}

.quote-dialog .upload-content {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	width: 100%;
	height: 100%;
}

.quote-dialog .upload-hint {
	color: #8c939d;
	font-size: 14px;
	margin-top: 8px;
}

.quote-dialog .el-input-group__prepend {
	background-color: #f5f7fa;
	color: #909399;
	border: 1px solid #dcdfe6;
}
</style>
<style>
#pane-payment-terms .ql-editor,
#pane-inclusions .ql-editor {
	min-height: 280px;
}
</style>